// liveCapabilityAnalytics.ts — CORE-776 (родитель CORE-767)
//
// Типизированные emit-хелперы для сквозной аналитики УРОВНЕВОГО СЛОЯ Live:
// «уровень получен → возможность доступна → показана → действие начато →
// завершено или отклонено». Тонкий слой над `analytics.track(...)`
// (→ существующий `AnalyticsEvents`), НЕ новый стрим — ровно как
// `shareAnalytics.ts` (CORE-788) и `gamificationAnalytics.ts` (CORE-711).
//
// ── Почему отдельный файл, но НЕ отдельный реестр ───────────────────────────
// Имена событий живут в едином каталоге `LiveSessionEvents`
// (`liveSessionAnalytics.ts`) — второго реестра не заводим (требование задачи).
// Здесь — только дисциплина контракта над теми именами: перечислимые свойства,
// общий билдер, приватный бэкстоп и деду́п. BE-зеркало имён —
// `Agape.Shared/Models/AnalyticsEventNames.cs` (`AnalyticsEventNames.Live`);
// держим списки в lock-step.
//
// ── Почему словарь типизирован ─────────────────────────────────────────────
// Гейт-точек много (create, schedule, announce, модерация, cohosts), и опечатка
// ключа не роняет сборку — она молча плодит строку, которую никто не считает.
// Поэтому каждый хелпер берёт типизированный объект: неверный ключ = ошибка
// компиляции, а не пропавший срез отчёта.
//
// ── Приватность (задача: НЕ передавать PII, название/описание Live, тексты
// чата, содержимое практик, приватный ledger геймификации) ──────────────────
// Защита двойная, как в shareAnalytics/gamificationAnalytics: ни один хелпер не
// принимает параметр, который мог бы это нести, и `scrubCapabilityProps`
// вычищает такие ключи из КАЖДОГО payload бэкстопом — поздний небрежный
// `{ ...props, title }` на call-site не протечёт. Уровень «Проводник света» и
// подтверждённая роль мастера — РАЗНЫЕ свойства (`levelCode` vs
// `isVerifiedMaster`, CORE-769 §1.1): их нельзя смешивать.

import { analytics } from './analytics';
import { buildParts } from '../config/buildInfo';
import { LiveSessionEvents } from './liveSessionAnalytics';
import type { LiveSessionEventName } from './liveSessionAnalytics';
import type { LiveCapabilitiesResponse, SpiritualLevelCode } from '../types/auth';

// ── Перечислимые свойства ───────────────────────────────────────────────────

/** Нормализованный код уровня Путь Света. `null` = уровень неприменим. */
export type LevelCode = SpiritualLevelCode; // 'seeker' | 'keeper' | 'master' | 'grand_master'

const LEVEL_CODES: readonly SpiritualLevelCode[] = ['seeker', 'keeper', 'master', 'grand_master'];

/**
 * Нормализует произвольную строку уровня к известному коду или `null`. Уровень
 * `master` здесь — это «Проводник света» (код уровня), а НЕ подтверждённая роль
 * мастера: роль несёт отдельное свойство `isVerifiedMaster` (CORE-769 §1.1).
 */
export function normalizeLevelCode(code: string | null | undefined): LevelCode | null {
  return code != null && LEVEL_CODES.includes(code as SpiritualLevelCode)
    ? (code as LevelCode)
    : null;
}

/** Где произошло действие/отказ. Одно значение на экран/точку входа. */
export type LiveFlowSurface =
  | 'create_screen'       // CreateLiveSessionScreen
  | 'start_user_session'  // StartUserSession (из диалога)
  | 'wizard'              // мастер шагов создания
  | 'live_room';          // внутри идущей сессии (LiveRoomScreen)

/**
 * Состояние жизненного цикла сессии на момент события. Ограниченный словарь —
 * чтобы отчёт группировался по стабильным значениям, а не по свободному тексту.
 */
export type SessionLifecycleState =
  | 'preview'    // до создания — динамическое превью прав
  | 'created'    // мгновенная сессия создана
  | 'scheduled'  // запланирована
  | 'started'    // запущена (идёт)
  | 'ended'      // завершена
  | 'denied';    // действие отклонено сервером

/** Как отличается клиентский показ от серверного отказа (рассинхрон). */
export type DesyncKind =
  | 'client_shown_server_denied' // клиент считал ключ у себя, снимок сервера отказал
  | 'contract_version_mismatch'; // версия динамического превью ≠ версии снимка

/**
 * Тело 403 по capability (CORE-819): сервер сообщает, чего не хватило. PII здесь
 * нет — только capability и коды уровней. Совпадает с `isCapabilityDenied`
 * (`errorMessages.ts`).
 */
export interface CapabilityDenial {
  capability: string;
  requiredLevelCode?: string | null;
  requiresMasterRole?: boolean | null;
  currentLevelCode?: string | null;
}

/**
 * Нормализованный код причины отказа — из тела 403, без свободного текста.
 * Роль важнее уровня: если требуется подтверждённая роль мастера, это отдельная
 * причина, а не «низкий уровень» (CORE-769 §1.1).
 */
export type DenyReasonCode = 'master_role_required' | 'level_required' | 'capability_required';

export function denyReasonCode(denial: CapabilityDenial): DenyReasonCode {
  if (denial.requiresMasterRole) return 'master_role_required';
  if (denial.requiredLevelCode) return 'level_required';
  return 'capability_required';
}

// ── Общий контекст и билдер свойств ─────────────────────────────────────────

/**
 * Общие технические свойства уровневого слоя, снятые с ответа capability. Два
 * РАЗНЫХ свойства — код уровня и подтверждённая роль мастера — не смешиваются:
 * `isVerifiedMaster` = роль (`creatorRole === 'master'`), `levelCode` = уровень.
 */
export interface CapabilityContext {
  /** Нормализованный код уровня, или null (мастер/слой выключен). */
  levelCode: LevelCode | null;
  /** Подтверждённая роль мастера — ОТДЕЛЬНОЕ свойство, не уровень. */
  isVerifiedMaster: boolean;
  /** Версия capability-контракта этого ответа. */
  contractVersion: number;
  /** Внёс ли уровневый слой вклад в ответ — это и есть «когорта выкатки». */
  levelLayerEnabled: boolean;
}

/**
 * Снять общий контекст с ответа capability (динамическое превью или снимок).
 */
export function capabilityContext(response: LiveCapabilitiesResponse): CapabilityContext {
  return {
    levelCode: normalizeLevelCode(response.creatorLevel),
    isVerifiedMaster: response.creatorRole === 'master',
    contractVersion: response.contractVersion,
    levelLayerEnabled: response.levelLayerEnabled,
  };
}

/**
 * Когорта выкатки. Инфраструктуры процентной/бакетной раскатки на бэке нет
 * (глобальный булев флаг + per-user override, без процентов — см. CORE-775),
 * поэтому честная «когорта» = состояние флага уровневого слоя, а не бакет.
 * Фабриковать несуществующую когорту нельзя.
 */
export type FlagCohort = 'level_layer_on' | 'level_layer_off';

/**
 * Свойства уровневого слоя для обогащения СУЩЕСТВУЮЩИХ Live-событий (например
 * `live_session_created`), чтобы связать создание/запуск с уровнем и версией
 * контракта. Держит «знание об уровне» В аналитическом слое: экран создания
 * отдаёт лишь ответ capability и НИКОГДА не выводит доступность из уровня/роли
 * (CORE-769 §запрет 2/3 — гвардовый тест parity следит, чтобы экран не поминал
 * levelCode/creatorLevel). isVerifiedMaster — роль, не уровень (§1.1).
 */
export function capabilityLevelProps(context: CapabilityContext): Record<string, unknown> {
  return {
    levelCode: context.levelCode,
    isVerifiedMaster: context.isVerifiedMaster,
    contractVersion: context.contractVersion,
    flagCohort: (context.levelLayerEnabled ? 'level_layer_on' : 'level_layer_off') as FlagCohort,
  };
}

function baseProps(ctx: CapabilityContext): Record<string, unknown> {
  const { platform, version } = buildParts();
  return {
    levelCode: ctx.levelCode,
    isVerifiedMaster: ctx.isVerifiedMaster,
    contractVersion: ctx.contractVersion,
    levelLayerEnabled: ctx.levelLayerEnabled,
    flagCohort: (ctx.levelLayerEnabled ? 'level_layer_on' : 'level_layer_off') as FlagCohort,
    platform,
    clientVersion: version,
  };
}

// ── Приватный бэкстоп ───────────────────────────────────────────────────────

/**
 * Ключи, которым нельзя попадать в `AnalyticsEvents`: identity, свободный текст
 * названия/описания Live, тексты чата, содержимое практик, приватный ledger
 * геймификации (xp, история) и invite-code (с CORE-721 это capability, урок
 * `describeEntityId`). Отчёту нужно знать, ЧТО функция открылась и использована,
 * а не как называется Live и не сколько у пользователя опыта.
 */
const FORBIDDEN_KEYS = [
  'name', 'displayName', 'email', 'phone', 'userName',
  'title', 'text', 'message', 'description', 'practiceTitle', 'query',
  'inviteCode', 'code', 'url', 'link',
  'xp', 'currentXp', 'history', 'activity', 'ledger',
];

/** Убрать identity / свободный текст / приватный ledger. Экспорт для теста. */
export function scrubCapabilityProps<T extends Record<string, unknown>>(props: T): T {
  const out = { ...props };
  for (const key of Object.keys(out)) {
    if (FORBIDDEN_KEYS.includes(key)) delete out[key];
  }
  return out;
}

function emit(event: LiveSessionEventName, props: Record<string, unknown>): void {
  analytics.track(event, scrubCapabilityProps(props) as Record<string, any>);
}

// ── Деду́п получения capability set ──────────────────────────────────────────
//
// Поллер прав бьёт каждые ~45s (`useLiveCapabilities` REFRESH_MS) и на каждом
// возврате в foreground. Слать `live_capabilities_resolved` на каждый резолв —
// это тысячи строк-дублей в день на пользователя. Шлём ТОЛЬКО когда набор
// реально изменился: сигнатура = версия + состояние флага + уровень +
// отсортированные ключи. Это и есть тест на дедупликацию из AC.

const lastResolvedSignature = new Map<string, string>();

/** Стабильная сигнатура набора прав — меняется, лишь когда меняется сам набор. */
export function capabilitySetSignature(response: LiveCapabilitiesResponse): string {
  const caps = [...response.capabilities].sort().join(',');
  return [
    response.contractVersion,
    response.levelLayerEnabled ? 1 : 0,
    normalizeLevelCode(response.creatorLevel) ?? '',
    response.creatorRole,
    caps,
  ].join('|');
}

/** Сбросить деду́п-состояние (только для тестов). */
export function __resetCapabilityAnalyticsState(): void {
  lastResolvedSignature.clear();
}

function dedupKey(userId: string | null | undefined): string {
  return userId == null || userId === '' ? 'anon' : String(userId);
}

// ── Emit-хелперы ────────────────────────────────────────────────────────────

/**
 * Получение или обновление capability set. Деду́п обязателен: событие уходит
 * ТОЛЬКО когда набор реально изменился с прошлого резолва этого пользователя.
 * Возвращает true, если событие отправлено (набор изменился), иначе false —
 * так call-site может судить, был ли это действительно новый набор.
 */
export function trackCapabilitiesResolved(args: {
  userId?: string | null;
  response: LiveCapabilitiesResponse;
}): boolean {
  const key = dedupKey(args.userId);
  const signature = capabilitySetSignature(args.response);
  if (lastResolvedSignature.get(key) === signature) return false;
  lastResolvedSignature.set(key, signature);

  const ctx = capabilityContext(args.response);
  emit(LiveSessionEvents.capabilitiesResolved, {
    ...baseProps(ctx),
    capabilities: [...args.response.capabilities],
    capabilityCount: args.response.capabilities.length,
    lockedCount: args.response.locked.length,
  });
  return true;
}

/**
 * Показ новой доступной функции — «показано», отдельно от «выдано». Ключи из
 * `selectNewCapabilities` (уже отфильтрованы от role-only и размечены).
 */
export function trackCapabilityUnlockShown(args: {
  context: CapabilityContext;
  capabilityKeys: string[];
}): void {
  emit(LiveSessionEvents.capabilityUnlockShown, {
    ...baseProps(args.context),
    capabilityKeys: [...args.capabilityKeys],
    capabilityCount: args.capabilityKeys.length,
  });
}

/** Открытие Live-flow с текущей версией контракта. */
export function trackLiveFlowOpened(args: {
  context: CapabilityContext;
  surface: LiveFlowSurface;
}): void {
  emit(LiveSessionEvents.flowOpened, {
    ...baseProps(args.context),
    surface: args.surface,
  });
}

/** Использование конкретной capability на гейт-точке. */
export function trackCapabilityUsed(args: {
  context: CapabilityContext;
  capabilityKey: string;
  lifecycleState?: SessionLifecycleState;
  surface?: LiveFlowSurface;
}): void {
  emit(LiveSessionEvents.capabilityUsed, {
    ...baseProps(args.context),
    capabilityKey: args.capabilityKey,
    ...(args.lifecycleState ? { lifecycleState: args.lifecycleState } : {}),
    ...(args.surface ? { surface: args.surface } : {}),
  });
}

/**
 * Клиентская запись серверного отказа по capability (403, CORE-819). Свойства
 * только из тела отказа — там PII нет. Зеркалит `be.capability_denied` на бэке.
 * `context` необязателен: отказ может прийти и без готового ответа capability.
 */
export function trackCapabilityDenied(args: {
  context?: CapabilityContext;
  denial: CapabilityDenial;
  surface?: LiveFlowSurface;
}): void {
  const ctx = args.context;
  emit(LiveSessionEvents.capabilityDenied, {
    ...(ctx ? baseProps(ctx) : {}),
    capabilityKey: args.denial.capability,
    reasonCode: denyReasonCode(args.denial),
    requiredLevelCode: normalizeLevelCode(args.denial.requiredLevelCode),
    requiresMasterRole: !!args.denial.requiresMasterRole,
    ...(args.surface ? { surface: args.surface } : {}),
  });
}

/**
 * Права расширились после level-up — якорь воронки к `level_up` (CORE-711).
 * `openedKeys` — то, что реально открылось (из `selectNewCapabilities`).
 */
export function trackCapabilitiesLeveledUp(args: {
  context: CapabilityContext;
  openedKeys: string[];
  previousLevelCode?: string | null;
  newLevelCode?: string | null;
}): void {
  emit(LiveSessionEvents.capabilitiesLeveledUp, {
    ...baseProps(args.context),
    openedKeys: [...args.openedKeys],
    openedCount: args.openedKeys.length,
    previousLevelCode: normalizeLevelCode(args.previousLevelCode),
    newLevelCode: normalizeLevelCode(args.newLevelCode),
  });
}

/**
 * Рассинхрон клиента и бэкенда: клиент показал возможность доступной, а сервер
 * отклонил её (либо версия превью ≠ версии снимка). Отделяет «клиентский показ»
 * от «серверного отказа» (AC).
 */
export function trackCapabilityDesync(args: {
  context: CapabilityContext;
  capabilityKey: string;
  kind: DesyncKind;
  snapshotContractVersion?: number | null;
}): void {
  emit(LiveSessionEvents.capabilityDesync, {
    ...baseProps(args.context),
    capabilityKey: args.capabilityKey,
    desyncKind: args.kind,
    ...(args.snapshotContractVersion != null
      ? { snapshotContractVersion: args.snapshotContractVersion }
      : {}),
  });
}
